/**
 * Design System Tokens - Unified design constants
 * Ensures consistency across all pages and components
 */

export const DesignTokens = {
  // Typography Scale - World-class unified system (Inter-based)
  typography: {
    // Font Families - Modern, readable, system-optimized
    fontFamily: {
      // Primary: Inter (world-class readability) with system font fallbacks
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
      // Heading: Same as primary for consistency (Inter works great for headings)
      heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
      // Mono: For code/technical content
      mono: '"SF Mono", "Monaco", "Cascadia Code", "Roboto Mono", "Courier New", monospace'
    },
    
    // Font Sizes - WCAG 2.1 AA compliant, semantic-first
    fontSize: {
      // Semantic Sizes (Desktop/Web)
      h1: '2rem',         // 32px - Page Title (H1)
      h2: '1.5rem',       // 24px - Section Title (H2)
      h3: '1.25rem',      // 20px - Subsection (H3)
      h4: '1.125rem',     // 18px - Subsection (H4)
      body: '1rem',       // 16px - Primary body text
      bodySecondary: '0.875rem', // 14px - Secondary body text
      label: '1rem',      // 16px - Labels, buttons (desktop)
      caption: '0.75rem', // 12px - Captions, hints

      // Legacy tokens (mapped to semantic equivalents)
      xs: '0.75rem',      // 12px - Caption/Hint
      sm: '0.875rem',     // 14px - Body Secondary
      base: '1rem',       // 16px - Body Primary
      lg: '1.125rem',     // 18px - H4
      xl: '1.25rem',      // 20px - H3
      '2xl': '1.5rem',    // 24px - H2
      '3xl': '2rem',      // 32px - H1 (CORRECTED from 30px)

      // Display/Hero sizes
      '4xl': '2.25rem',   // 36px - Large display
      '5xl': '3rem',      // 48px - Hero (lower bound)
      '6xl': '3.5rem'     // 56px - Hero (upper bound, CORRECTED from 60px)
    },
    
    // Font Weights - Accessible, readable weights
    fontWeight: {
      light: 300,         // Rarely used
      normal: 400,        // Body text
      medium: 500,        // Emphasized text, buttons
      semibold: 600,      // Headings, strong emphasis
      bold: 700,          // Hero headings, important text
      extrabold: 800      // Rarely used
    },
    
    // Line Heights - WCAG 2.1 AA compliant
    lineHeight: {
      heading: 1.25,      // H1-H4 (REQUIRED: 1.25)
      body: 1.6,          // Body text (REQUIRED: 1.6)
      display: 1.2,       // Display/Hero text only
      relaxed: 1.75,      // Long-form content (optional)

      // Legacy tokens (mapped to compliant values)
      tight: 1.25,        // CORRECTED from 1.2 → Maps to heading
      normal: 1.6,        // CORRECTED from 1.5 → Maps to body
      loose: 2            // Preserved for edge cases
    },
    
    // Letter Spacing - Subtle adjustments for readability
    letterSpacing: {
      tight: '-0.01em',  // Large headings
      normal: '0',        // Default
      wide: '0.025em'     // Uppercase text, labels
    }
  },

  // Spacing Scale - Consistent spacing
  spacing: {
    0: '0',
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '2.5rem', // 40px
    '3xl': '3rem',   // 48px
    '4xl': '4rem',   // 64px
    '5xl': '5rem'    // 80px
  },

  // Border Radius - Consistent rounded corners
  borderRadius: {
    none: '0',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    full: '9999px'
  },

  // Image Aspect Ratios - Consistent image sizes
  aspectRatio: {
    square: '1 / 1',
    landscape: '4 / 3',      // Standard card images
    portrait: '3 / 4',
    wide: '16 / 9',          // Hero images
    ultrawide: '21 / 9'
  },

  // Image Sizes - Standard image dimensions
  imageSize: {
    thumbnail: '80px',
    small: '120px',
    medium: '240px',
    large: '400px',
    xlarge: '600px',
    hero: '1920px'
  },

  // Container Widths
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1200px',
    '2xl': '1280px'
  },

  // Z-Index Scale
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modal: 1300,
    popover: 1400,
    tooltip: 1500
  },

  // Transitions - Consistent animation timing
  transition: {
    fast: '0.15s',
    normal: '0.2s',
    slow: '0.3s',
    slower: '0.5s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easingBounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  // Shadows - Material Design elevation
  shadow: {
    sm: '0 1px 2px rgba(60, 64, 67, 0.08)',
    md: '0 2px 8px rgba(60, 64, 67, 0.08)',
    lg: '0 4px 12px rgba(60, 64, 67, 0.12)',
    xl: '0 8px 24px rgba(60, 64, 67, 0.16)',
    '2xl': '0 12px 32px rgba(60, 64, 67, 0.2)'
  }
};

export default DesignTokens;

